/**
 * Centralized Design Tokens for WES Hub 360
 * All design values should be referenced from this file to maintain consistency
 * @module designTokens
 */

// Brand Colors - WES Hub 360 Theme
export const colors = {
  primary: {
    main: '#016FF4',      // Blue/05 - Principal
    light: '#38BCFF',     // Blue/01 - Mais claro
    dark: '#001035',      // Blue/06 - Mais escuro
    gradient: 'linear-gradient(180deg, #38BCFF 0%, #20A4FE 25%, #1991FA 50%, #0B82F9 75%, #016FF4 100%)',
  },
  // Escala de Azuis WES
  blue: {
    '01': '#38BCFF',
    '02': '#20A4FE',
    '03': '#1991FA',
    '04': '#0B82F9',
    '05': '#016FF4',
    '06': '#001035',
  },
  secondary: {
    main: '#20A4FE',      // Blue/02
    light: '#38BCFF',
    dark: '#0B82F9',
  },
  accent: {
    pink: '#F7658E',
    pinkLight: '#FEF3F6',
    iconBg: '#EFF7FE',
    blue: '#20A4FE',
  },
  semantic: {
    success: 'rgba(1, 111, 244, 0.6)',  // Azul com opacidade
    successSolid: '#4CAF50',
    warning: '#FFA726',
    error: '#F44336',
    danger: '#CE3535',
    info: '#016FF4',
    critical: '#F44336',
  },
  background: {
    main: '#EEEFF0',      // Fundo principal
    sidebar: '#F7F8F9',   // Fundo sidebar
    content: '#FFFFFF',   // Fundo conteudo
    paper: '#FFFFFF',
  },
  border: {
    default: '#DFDFDF',
    light: '#EBEBEB',
    divider: '#D9D9D9',
  },
  neutral: {
    white: '#FFFFFF',
    black: '#000000',
    gray: {
      50: '#F7F8F9',
      100: '#EEEFF0',
      200: '#DFDFDF',
      300: '#D9D9D9',
      400: '#D0D0D0',
      500: '#9aa0a6',
      600: '#80868b',
      700: '#5f6368',
      800: '#444444',
      900: '#202124',
    },
  },
  text: {
    primary: '#000000',
    secondary: '#444444',
    disabled: '#9aa0a6',
    inverse: '#FFFFFF',
  },
};

/** WES dark surfaces — style guide: dark base #000000, elevated layers for readability */
export const colorsDark = {
  primary: colors.primary,
  secondary: colors.secondary,
  blue: colors.blue,
  accent: {
    pink: '#F7658E',
    pinkLight: 'rgba(247, 101, 142, 0.14)',
    iconBg: 'rgba(1, 111, 244, 0.18)',
    blue: '#38BCFF',
  },
  semantic: {
    ...colors.semantic,
  },
  background: {
    main: '#000000',
    sidebar: '#0A0A0A',
    content: '#000000',
    paper: '#121212',
  },
  border: {
    default: '#2A2A2A',
    light: '#1E1E1E',
    divider: '#2C2C2C',
  },
  neutral: {
    white: '#FFFFFF',
    black: '#000000',
    gray: {
      50: '#141414',
      100: '#181818',
      200: '#1E1E1E',
      300: '#2A2A2A',
      400: '#3C4043',
      500: '#5f6368',
      600: '#80868b',
      700: '#9aa0a6',
      800: '#bdc1c6',
      900: '#e8eaed',
    },
  },
  text: {
    primary: '#F1F3F4',
    secondary: '#B8BCC4',
    disabled: '#6B7280',
    inverse: '#000000',
  },
};

/** localStorage key — must match inline script in public/index.html */
export const STORAGE_THEME_KEY = 'hub360_color_mode';

/**
 * Stored value may be explicit light/dark or "auto" (follow OS).
 * Invalid/missing → treat as auto (matches FOUC script in public/index.html).
 */
export function getStoredThemePreference() {
  if (typeof window === 'undefined') return 'auto';
  try {
    const stored = window.localStorage.getItem(STORAGE_THEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  } catch {
    /* ignore */
  }
  return 'auto';
}

/** Effective palette mode for MUI/CSS (always light or dark). */
export function resolveEffectiveColorMode(preference, systemIsDark) {
  if (preference === 'dark' || preference === 'light') return preference;
  return systemIsDark ? 'dark' : 'light';
}

/**
 * One-shot effective mode (e.g. tests or legacy callers).
 * Prefer ThemeContext for reactive updates when preference is `auto`.
 */
export function getResolvedColorMode() {
  if (typeof window === 'undefined') return 'light';
  const pref = getStoredThemePreference();
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  const systemDark = Boolean(mq?.matches);
  return resolveEffectiveColorMode(pref, systemDark);
}

// Typography Scale - Inter Font
export const typography = {
  fontFamily: {
    // Matches dashboard-static + wes-style-guide body rule so MUI
    // Typography renders with the same fallback chain as the raw-HTML
    // surfaces. Inter is the canonical WES face; Roboto was here as a
    // fallback from CRA defaults and has been swapped for Segoe UI (the
    // Windows system stack the designer references).
    primary: "'Inter', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    monospace: '"Fira Code", "Monaco", "Courier New", monospace',
  },
  fontSize: {
    xs: '10px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.1,
    normal: 1.5,       // 150%
    relaxed: 1.75,
  },
  letterSpacing: {
    tight: '-0.01em',
    normal: '0',
    wide: '0.02em',
  },
};

// Spacing Scale (based on 8px grid)
export const spacing = {
  0: 0,
  1: 8,
  2: 16,
  3: 24,
  4: 32,
  5: 40,
  6: 48,
  7: 56,
  8: 64,
  9: 72,
  10: 80,
  12: 96,
  16: 128,
  20: 160,
  24: 192,
  32: 256,
};

// Breakpoints
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// Border Radius
export const borderRadius = {
  none: 0,
  sm: 2,
  base: 4,
  md: 6,
  lg: 8,           // Padrao WES
  xl: 12,
  '2xl': 16,
  '3xl': 24,
  full: '50%',
  pill: '9999px',
  // Especifico para sidebar item ativo
  menuActive: '8px 0px 0px 8px',
};

// Shadows
export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px rgba(0, 0, 0, 0.15)',
  brand: '0 8px 32px rgba(1, 111, 244, 0.3)',
  brandHover: '0 12px 48px rgba(1, 111, 244, 0.4)',
  card: '0 4px 20px rgba(0, 0, 0, 0.08)',
};

/** Dark mode elevations — must be declared after `shadows` */
export const shadowsDark = {
  ...shadows,
  sm: '0 1px 2px rgba(0, 0, 0, 0.4)',
  base: '0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.35)',
  md: '0 4px 6px rgba(0, 0, 0, 0.35), 0 2px 4px rgba(0, 0, 0, 0.28)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.35), 0 4px 6px rgba(0, 0, 0, 0.25)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.4), 0 10px 10px rgba(0, 0, 0, 0.25)',
  '2xl': '0 25px 50px rgba(0, 0, 0, 0.45)',
  brand: '0 8px 32px rgba(1, 111, 244, 0.35)',
  brandHover: '0 12px 48px rgba(1, 111, 244, 0.45)',
  card: '0 4px 24px rgba(0, 0, 0, 0.45)',
};

// Transitions
export const transitions = {
  duration: {
    fast: '150ms',
    base: '300ms',
    slow: '600ms',
    slower: '1000ms',
  },
  easing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
};

// Z-Index Scale
export const zIndex = {
  negative: -1,
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modalBackdrop: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
  toast: 1700,
};

// Animation Keyframes
export const animations = {
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeUp: {
    from: {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  slideLeft: {
    from: {
      opacity: 0,
      transform: 'translateX(-30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  slideRight: {
    from: {
      opacity: 0,
      transform: 'translateX(30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  scale: {
    from: {
      opacity: 0,
      transform: 'scale(0.8)',
    },
    to: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  shimmer: {
    from: {
      backgroundPosition: '-200% 0',
    },
    to: {
      backgroundPosition: '200% 0',
    },
  },
};

// Gradients
export const gradients = {
  brand: colors.primary.gradient,
  brandVertical: 'linear-gradient(180deg, #38BCFF 0%, #20A4FE 25%, #1991FA 50%, #0B82F9 75%, #016FF4 100%)',
  light: 'linear-gradient(180deg, #F7F8F9 0%, #FFFFFF 100%)',
  accent: 'linear-gradient(135deg, #38BCFF 0%, #016FF4 100%)',
  sidebar: 'linear-gradient(180deg, #F7F8F9 0%, #EEEFF0 100%)',
};

// Component-specific tokens
export const components = {
  button: {
    height: {
      sm: 32,
      md: 40,
      lg: 48,
    },
    padding: {
      sm: '8px 16px',
      md: '12px 24px',
      lg: '16px 32px',
    },
  },
  card: {
    padding: spacing[3],
    borderRadius: borderRadius.lg,
    shadow: shadows.card,
    border: `2px solid ${colors.border.default}`,
  },
  input: {
    height: 40,
    borderRadius: borderRadius.md,
    borderColor: colors.border.default,
  },
  sidebar: {
    width: 280,
    widthCollapsed: 70,
    background: colors.background.sidebar,
    borderColor: colors.border.divider,
    itemHeight: 48,
    itemPadding: '10px 24px',
    itemBorderRadius: '8px 0px 0px 8px',
  },
  navbar: {
    height: 64,
    background: colors.background.content,
  },
  menuItem: {
    active: {
      background: colors.primary.gradient,
      color: colors.neutral.white,
      borderRadius: '8px 0px 0px 8px',
    },
    inactive: {
      background: 'transparent',
      color: colors.primary.main,
    },
    logout: {
      color: colors.semantic.danger,
    },
  },
};

// Export all tokens as a single object for convenience
const designTokens = {
  colors,
  typography,
  spacing,
  breakpoints,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  animations,
  gradients,
  components,
};

export default designTokens;
